<script lang="ts">
  import { onMount } from "svelte";
  import { getWithJwt, serverlessRoutes, translate } from "lib";
  import { CheckboxComponent } from "ui";
  import Spinner from "../UI/Spinner.svelte";
  import WinbackClientCard from "./WinbackClientCard.svelte";
  import type { WinbackClient } from "../../interfaces/Winback";

  let isLoading = true;
  let hasError = false;
  let clients: WinbackClient[] = [];

  // Archived clients were deliberately offboarded by the trainer, so they are
  // hidden until the trainer opts in — mirrors the "Display archived" filter
  // used elsewhere in the app. Default: expired clients only.
  let showArchived = false;

  const fetchWinbackClients = async (): Promise<void> => {
    isLoading = true;
    hasError = false;

    try {
      const url = showArchived
        ? `${serverlessRoutes.WINBACK_CLIENTS}?includeArchived=1`
        : serverlessRoutes.WINBACK_CLIENTS;
      const res = await getWithJwt(url);
      clients = Array.isArray(res?.data) ? res.data : [];
    } catch (err) {
      hasError = true;
    } finally {
      isLoading = false;
    }
  };

  const toggleArchived = (): void => {
    if (isLoading) return;
    showArchived = !showArchived;
    fetchWinbackClients();
  };

  onMount(fetchWinbackClients);
</script>

<div class="w-full text-slate-900 dark:text-slate-100">
  <header class="px-4 pt-4 pb-3 border-b border-slate-200 dark:border-zinc-700">
    <h2 class="text-lg font-semibold">{translate("WINBACK")}</h2>
    <p class="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
      {translate("WINBACK_SUBTITLE")}
    </p>
  </header>

  <div class="flex flex-row items-center px-4 py-3 border-b border-slate-200 dark:border-zinc-700">
    <div on:click={toggleArchived}>
      <CheckboxComponent value={showArchived} />
    </div>
    <span class="ml-2 text-sm">{translate("WINBACK_SHOW_ARCHIVED")}</span>
  </div>

  {#if isLoading}
    <Spinner height="50vh" />
  {:else if hasError}
    <div class="flex flex-col items-center justify-center text-center gap-3 py-16 px-4">
      <p class="text-sm text-slate-600 dark:text-slate-400">
        {translate("WINBACK_ERROR")}
      </p>
      <button
        type="button"
        class="px-4 py-2 rounded-md text-sm font-medium bg-teal-600 text-white hover:bg-teal-700 transition-colors"
        on:click={fetchWinbackClients}
      >
        {translate("TRY_AGAIN")}
      </button>
    </div>
  {:else if clients.length === 0}
    <div class="flex flex-col items-center justify-center text-center gap-1 py-16 px-4">
      <p class="text-sm font-medium text-slate-600 dark:text-slate-300">
        {translate("WINBACK_EMPTY_TITLE")}
      </p>
      <p class="text-xs text-slate-500 dark:text-slate-400">
        {translate("WINBACK_EMPTY_SUBTITLE")}
      </p>
    </div>
  {:else}
    <div class="flex flex-col">
      {#each clients as client (client.client_id)}
        <WinbackClientCard {client} />
      {/each}
    </div>
  {/if}
</div>
